import {
  FaArrowUp,
  FaArrowDown,
  FaShoppingBag,
  FaUsers,
  FaWallet,
  FaMoneyBillWave,
  FaCheck,
} from "react-icons/fa";
import { IoCalendarClearOutline } from "react-icons/io5";
import { PolarArea, Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineElement,
  PointElement,
  LineController,
  Title,
} from "chart.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TbRefresh } from "react-icons/tb";
import axiosInstance from "../../api/axiosInstance";
import { FaWindowMaximize, FaTools, FaCubes } from "react-icons/fa";
import Select, { components } from "react-select";
import CountUp from "react-countup";
import formatDateIndian from "../../common/formatDateIndian";

const Dashboard = () => {
  const navigate = useNavigate();
  const [dealType, setDealType] = useState("window");
  const [lostDeals, setLostDeals] = useState([]);
  const [statusBreakdownData, setStatusBreakdownData] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const userId = localStorage.getItem("userId");
  const [tasks, setTasks] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [inProductionData, setInProductionData] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [selectedSalePersonId, setSelectedSalePersonId] = useState(
    userId === "1" ? "" : userId
  );
  const [todayDeals, setTodayDeals] = useState([]);
  const storedUserName = localStorage.getItem("userName");
  const [totalLeads, setTotalLeads] = useState([]);
  const [stageSummary, setStageSummary] = useState([]);
  const [topArchitects, setTopArchitects] = useState([]);
  const [dateFilter, setDateFilter] = useState("thisWeek");

  ChartJS.register(
    RadialLinearScale,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    BarController,
    LineController,
    Title
  );
  const labels = monthlyData.map((item) => item.name);

  const getDateRange = (filter) => {
    const now = new Date();
    const start = new Date();
    const end = new Date();

    if (filter === "thisWeek") {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      start.setDate(diff);
      end.setDate(diff + 6);
    } else if (filter === "thisMonth") {
      start.setDate(1);
      end.setMonth(now.getMonth() + 1);
      end.setDate(0);
    } else if (filter === "thisYear") {
      start.setMonth(0);
      start.setDate(1);
      end.setMonth(11);
      end.setDate(31);
    }

    return {
      fromDate: start.toISOString().split("T")[0] + "T00:00:00",
      toDate: end.toISOString().split("T")[0] + "T23:59:59",
    };
  };

  const data = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "Active",
        data: monthlyData.map((item) => item.active),
        backgroundColor: "#14b8a6",
        borderSkipped: false,
        barThickness: 40,
        maxBarThickness: 40,
        borderRadius: 0,
      },

      {
        type: "line",
        label: "Complete",
        data: monthlyData.map((item) => item.complete),
        borderColor: "#6366f1",
        backgroundColor: "#6366f1",
        borderWidth: 2,
        // tension: 0.3,
        // pointRadius: 4,
        pointBackgroundColor: "#6366f1",
        fill: false,
      },
      {
        type: "line",
        label: "Close Lost",
        data: monthlyData.map((item) => item.closeLost),
        borderColor: "#f97316",
        backgroundColor: "#f97316",
        borderWidth: 2,
        borderDash: [5, 5],
        tension: 0.3,
        pointRadius: 4,
        pointBackgroundColor: "#f97316",
        fill: false,
      },
    ],
  };

  const optionsBar = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.raw || 0;
            return `${label}: ₹${Number(value).toLocaleString("en-IN")}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `₹${value / 100000}L`,
          maxTicksLimit: 6,
          padding: 10,
        },
        grid: { drawBorder: false },
      },
      x: {
        ticks: { padding: 8 },
        grid: { display: false },
        categoryPercentage: 1.0,
        barPercentage: 1.0,
      },
    },
  };

  const fetchTopArchitects = async (type, salePersonId) => {
    try {
      const response = await axiosInstance.post("Dashboard/TopArchitects", {
        type,
        salePersonId: Number(salePersonId),
      });
      setTopArchitects(response.data);
    } catch (error) {
      console.error("Failed to fetch Top Architects", error);
    }
  };

  useEffect(() => {
    if (selectedSalePersonId) {
      fetchTopArchitects(dealType, selectedSalePersonId);
    }
  }, [dealType, selectedSalePersonId]);

  const fetchStageSummary = async () => {
    const { fromDate, toDate } = getDateRange(dateFilter);
    try {
      const response = await axiosInstance.post(
        "Dashboard/StageWisePerformanceSummary",
        {
          type: dealType,
          salePersonId: Number(selectedSalePersonId),
          fromDate,
          toDate,
        }
      );
      setStageSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch StageWise Summary", error);
    }
  };
  const stageConfig = {
    Active: {
      icon: <FaShoppingBag />,
      bg: "bg-[#eef2ff]",
      text: "text-[#4f46e5]",
      label: "ACTIVE",
    },
    CloseLost: {
      icon: <FaUsers />,
      bg: "bg-[#fff7ed]",
      text: "text-[#f97316]",
      label: "CLOSE LOST",
    },
    InProduction: {
      icon: <FaWallet />,
      bg: "bg-[#f1f4f9]",
      text: "text-[#405189]",
      label: "IN-PRODUCTION",
    },
    Complete: {
      icon: <FaCheck />,
      bg: "bg-[#f0fdf4]",
      text: "text-[#22c55e]",
      label: "COMPLETE",
    },
  };

  useEffect(() => {
    if (selectedSalePersonId) {
      fetchStageSummary();
      fetchTotalLeads();
      fetchMyTasks(selectedSalePersonId);
    }
  }, [dealType, selectedSalePersonId, dateFilter]);

  const fetchTotalLeads = async () => {
    const { fromDate, toDate } = getDateRange(dateFilter);
    try {
      const response = await axiosInstance.post("Dashboard/TotalLeadsOnly", {
        fromDate,
        toDate,
        salePersonId: Number(selectedSalePersonId),
      });
      const result = response.data;
      setTotalLeads(Array.isArray(result) ? result : [result]);
    } catch (error) {
      console.error("Failed to fetch Total Leads", error);
    }
  };

  useEffect(() => {
    if (selectedSalePersonId) {
      fetchTodayDealsStatus(dealType, selectedSalePersonId);
    }
  }, [dealType, selectedSalePersonId]);

  const fetchTodayDealsStatus = async (type, salePersonId) => {
    try {
      const response = await axiosInstance.post("Dashboard/DealsStatus", {
        type,
        salePersonId: Number(salePersonId),
      });
      setTodayDeals(response.data);
    } catch (error) {
      console.error("Failed to fetch Today's Deals Status", error);
    }
  };

  useEffect(() => {
    if (selectedSalePersonId) {
      fetchInProductionData(selectedSalePersonId);
    }
  }, [selectedSalePersonId]);

  const fetchInProductionData = async (salePersonId) => {
    try {
      const response = await axiosInstance.post("Dashboard/InProductionData", {
        salePersonId: Number(salePersonId),
      });
      setInProductionData(response.data);
    } catch (error) {
      console.error("Failed to fetch In-Production Data", error);
    }
  };

  const fetchTopCustomers = async (type, salePersonId) => {
    try {
      const response = await axiosInstance.post("Dashboard/TopCustomers", {
        type,
        salePersonId: Number(salePersonId),
      });
      setTopCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch Top Customers", error);
    }
  };

  const fetchMonthlyPerformance = async (type, salePersonId) => {
    try {
      const response = await axiosInstance.post(
        "Dashboard/monthlyPerformance",
        {
          type,
          salePersonId: Number(salePersonId),
        }
      );
      setMonthlyData(response.data);
    } catch (error) {
      console.error("Failed to fetch monthly performance:", error);
    }
  };

  useEffect(() => {
    if (selectedSalePersonId) {
      fetchMonthlyPerformance(dealType, selectedSalePersonId);
      fetchTopCustomers(dealType, selectedSalePersonId);
    }
  }, [dealType, selectedSalePersonId]);

  const fetchSalePersons = async () => {
    try {
      const { data } = await axiosInstance.get("User/GetAllUser");
      setSalesPersons(data);
    } catch (error) {
      console.error("Error fetching Sale Person", error);
    }
  };

  const polarChartData = {
    labels: ["Active", "Close Won", "In-Production", "Complete", "Close Lost"],
    datasets: [
      {
        label: "Status Distribution",
        data: statusBreakdownData,
        backgroundColor: [
          "#3b82f6",
          "#2dd4bf",
          "#facc15",
          "#9ca3af",
          "#f87171",
        ],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 15,
          padding: 15,
          color: "#405189",
          font: {
            family: "'Inter', sans-serif",
            size: 13,
          },
        },
      },
    },
  };

  useEffect(() => {
    lostData(dealType);
    fetchSalePersons();
  }, []);

  useEffect(() => {
    if (userId === "1" && salesPersons.length > 0 && !selectedSalePersonId) {
      const defaultId = salesPersons[0].sale_Person_Id?.toString();
      setSelectedSalePersonId(defaultId);
    }
  }, [salesPersons]);

  const lostData = async (type) => {
    try {
      const response = await axiosInstance.post("Dashboard/LostDeals", {
        type,
      });
      setLostDeals(response.data);
    } catch (error) {
      console.error("Error fetching lost deals:", error);
    }
  };

  useEffect(() => {
    if (selectedSalePersonId) {
      fetchStatusBreakdown(dealType);
    }
  }, [selectedSalePersonId, dealType]);

  const fetchStatusBreakdown = async (type) => {
    try {
      const response = await axiosInstance.post("Dashboard/statusBreakdown", {
        type,
        salePersonId: Number(selectedSalePersonId),
      });

      const statusArray = response.data;
      const labels = [
        "Active",
        "Close Won",
        "In-Production",
        "Complete",
        "Close Lost",
      ];
      const values = labels.map((label) => {
        const item = statusArray.find((entry) => entry.statusName === label);
        return item ? item.recordCount : 0;
      });

      setStatusBreakdownData(values);
    } catch (error) {
      console.error("Failed to fetch status breakdown", error);
    }
  };

  useEffect(() => {
    fetchMyTasks(selectedSalePersonId);
  }, [selectedSalePersonId]);

  const fetchMyTasks = async (salePersonId) => {
    const { fromDate, toDate } = getDateRange(dateFilter);
    try {
      setTaskLoading(true);
      const response = await axiosInstance.post("Dashboard/MyTasks", {
        fromDate,
        toDate,
        salePersonId: Number(salePersonId),
      });
      setTasks(response.data);
    } catch (error) {
      console.error("Failed to fetch My Tasks", error);
    } finally {
      setTaskLoading(false);
    }
  };

  const salePersonOptions = [
    ...salesPersons.map((per) => ({
      value: per.sale_Person_Id?.toString() || "",
      label: per.Sale_Person_Name || "Unnamed sale person",
    })),
  ];

  const renderInProductionTable = () => {
    if (
      !inProductionData?.summary ||
      !Array.isArray(inProductionData.summary) ||
      inProductionData.summary.length === 0
    ) {
      return (
        <tr>
          <td
            colSpan={5}
            className="text-center text-gray-400 py-6 font-medium"
          >
            No data available
          </td>
        </tr>
      );
    }

    return inProductionData.summary.map((summary, idx) => {
      const details = inProductionData.detail?.find(
        (item) => item.quotation_No === summary.quotation_No
      );

      const stages = [
        { label: "Measurement", value: details?.measur_Per ?? 0 },
        { label: "Production", value: details?.production_Per ?? 0 },
        { label: "Delivery", value: details?.delivery_Per ?? 0 },
        { label: "Fitting", value: details?.fitting_Per ?? 0 },
      ];

      return (
        <React.Fragment key={summary.quotation_No}>
          <tr
            className="border-b cursor-pointer hover:bg-gray-50 text-sm"
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
          >
            <td className="px-4 py-4 text-left font-semibold">
              QTN-{summary.quotation_No}
            </td>
            <td className="px-4 py-4">{summary.site_Name ?? "-"}</td>
            <td className="px-4 py-4">
              {details?.total_Window ?? summary.total_Window ?? 0}
            </td>
            <td className="px-4 py-4">
              ₹{Number(summary.final_Amount).toLocaleString("en-IN")}
            </td>
            <td className="px-4 py-4">{summary.complete_Per}%</td>
          </tr>

          {openIndex === idx && (
            <tr>
              <td colSpan={5} className="p-6 bg-gray-50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {stages.map((stage, i) => (
                    <div key={i}>
                      <div className="text-xs text-gray-600 mb-1">
                        {stage.label}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-teal-500 h-2.5 rounded-full transition-all duration-500"
                          style={{ width: `${stage.value}%` }}
                        ></div>
                      </div>
                      <div className="text-right text-xs text-gray-500 mt-1">
                        {stage.value}%
                      </div>
                    </div>
                  ))}
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  const statusColorMap = {
    active: "blue",
    "close won": "purple",
    "close lost": "red",
    "in production": "yellow",
    complete: "green",
  };

  const SingleValueWithIcon = (props) => (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        {props.data.icon}
        {props.data.label}
      </div>
    </components.SingleValue>
  );

  const OptionWithIcon = (props) => (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        {props.data.icon}
        {props.data.label}
      </div>
    </components.Option>
  );

  const dateOptions = [
    { value: "thisWeek", label: "This Week", icon: <IoCalendarClearOutline /> },
    {
      value: "thisMonth",
      label: "This Month",
      icon: <IoCalendarClearOutline />,
    },
    { value: "thisYear", label: "This Year", icon: <IoCalendarClearOutline /> },
  ];

  const dealTypeOptions = [
    { value: "window", label: "Window Quotation", icon: <FaWindowMaximize /> },
    { value: "manual", label: "Manual Quotation", icon: <FaTools /> },
    { value: "material", label: "Material Quotation", icon: <FaCubes /> },
  ];

  const customStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "white",
      borderColor: state.isFocused ? "#405189" : "rgba(0,0,0,0.05)",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(64, 81, 137, 0.1)" : "none",
      "&:hover": {
        borderColor: "#405189",
      },
      borderRadius: "0.85rem",
      padding: "2px 8px",
      minHeight: "42px",
      fontSize: "0.875rem",
      fontWeight: "600",
      color: "#405189",
      cursor: "pointer",
      transition: "all 0.2s ease",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#405189"
        : state.isFocused
          ? "rgba(64, 81, 137, 0.05)"
          : "white",
      color: state.isSelected ? "white" : "#4b5563",
      padding: "10px 15px",
      "&:active": {
        backgroundColor: "#405189",
        color: "white",
      },
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "1rem",
      overflow: "hidden",
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      border: "1px solid rgba(0,0,0,0.05)",
      marginTop: "8px",
      padding: "4px",
    }),
    singleValue: (base) => ({
      ...base,
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      color: "#405189",
      fontWeight: "700",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontWeight: "500",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#6b7280",
      "&:hover": { color: "#405189" },
    }),
  };

  const handleViewAllSites = async () => {
    const { fromDate, toDate } = getDateRange(dateFilter);

    try {
      const response = await axiosInstance.post("Dashboard/LeadSiteDetails", {
        fromDate,
        toDate,
        salePersonId: Number(selectedSalePersonId),
      });

      navigate("/transaction/sites", {
        state: {
          siteData: response.data,
          // totalRecords: response.data.totalRecords,
          isFilteredFromDashboard: true,
        },
      });
    } catch (error) {
      console.error("Failed to fetch Lead Site Details", error);
    }
  };

  const handleViewAllByStage = async (stage) => {
    const { fromDate, toDate } = getDateRange(dateFilter);

    let endpoint = "";
    switch (stage) {
      case "Active":
        endpoint = "Dashboard/StageWiseDeals-Active";
        break;
      case "CloseLost":
        endpoint = "Dashboard/StageWiseDeals-CloseLost";
        break;
      case "InProduction":
        endpoint = "Dashboard/StageWiseDeals-InProduction";
        break;
      case "Complete":
        endpoint = "Dashboard/StageWiseDeals-Complete";
        break;
      default:
        console.warn("Unknown stage");
        return;
    }

    try {
      const response = await axiosInstance.post(endpoint, {
        type: dealType,
        salePersonId: Number(selectedSalePersonId),
        fromDate,
        toDate,
      });

      const routeMap = {
        window: "/quotes/window-quotation",
        manual: "/quotes/manual-quotation",
        material: "/quotes/material-quotation",
      };

      navigate(routeMap[dealType], {
        state: {
          isFilteredFromDashboard: true,
          stage,
          filteredData: response?.data?.quotations,
          filterParams: {
            fromDate,
            toDate,
            salePersonId: selectedSalePersonId,
          },
        },
      });
    } catch (error) {
      console.error("Failed to fetch stage-wise deals:", error);
    }
  };
  const formatCountShort = (value) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return `${value}`;
  };

  return (
    <div className="animate-fade-in">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6 glass p-6 rounded-3xl border border-white/40 shadow-sm">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight">
            Welcome back, <span className="text-primary">{storedUserName}!</span>
          </h1>
          <p className="text-gray-500 font-medium mt-1">
            Here's what's happening with your projects today.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Salesperson Filter */}
          <div className="w-[200px]">
            {userId === "1" ? (
              <Select
                options={salePersonOptions}
                value={salePersonOptions.find((opt) => opt.value === selectedSalePersonId)}
                onChange={(selected) => setSelectedSalePersonId(selected?.value || "")}
                isClearable
                isSearchable
                classNamePrefix="react-select"
                className="text-sm shadow-sm"
                placeholder="Salesperson"
                styles={customStyles}
                menuPortalTarget={document.body}
              />
            ) : (
              <div className="bg-white border border-gray-100 px-4 py-2 rounded-xl text-sm font-bold text-primary shadow-sm min-w-[150px] text-center">
                {storedUserName}
              </div>
            )}
          </div>

          <div className="w-[180px]">
            <Select
              options={dateOptions}
              value={dateOptions.find((opt) => opt.value === dateFilter)}
              onChange={(selected) => setDateFilter(selected?.value || "")}
              isSearchable={false}
              classNamePrefix="react-select"
              className="text-sm shadow-sm"
              components={{ SingleValue: SingleValueWithIcon, Option: OptionWithIcon }}
              styles={customStyles}
              menuPortalTarget={document.body}
            />
          </div>

          <div className="w-[200px]">
            <Select
              options={dealTypeOptions}
              value={dealTypeOptions.find((opt) => opt.value === dealType)}
              onChange={(selected) => {
                const value = selected?.value || "";
                setDealType(value);
                lostData(value);
                fetchStatusBreakdown(value);
              }}
              isSearchable={false}
              classNamePrefix="react-select"
              className="text-sm shadow-sm"
              components={{ SingleValue: SingleValueWithIcon, Option: OptionWithIcon }}
              styles={customStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </div>
      </div>
      {/* cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {/* Total Leads Card */}
        <div className="premium-card p-6 flex flex-col justify-between group relative overflow-hidden h-[185px]">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[13px] font-bold text-gray-400 uppercase tracking-widest mb-1">TOTAL LEAD'S</p>
              <h2 className="text-3xl font-extrabold text-gray-800">
                <CountUp end={totalLeads?.[0]?.currentCount || 0} duration={1.2} separator="," />
              </h2>
              {(() => {
                const displayChange = totalLeads?.[0]?.displayChange || "0.00%";
                const isNegative = displayChange.includes("-") || totalLeads?.[0]?.isNegative;
                return (
                  <p className={`text-[12px] mt-2 flex items-center font-bold ${isNegative ? 'text-red-500' : 'text-green-500'}`}>
                    {isNegative ? <FaArrowDown /> : <FaArrowUp />}
                    <span className="ml-1">{displayChange}</span>
                  </p>
                );
              })()}
            </div>
            <div className="w-12 h-12 bg-[#e8eaf6] text-[#405189] rounded-full flex items-center justify-center shadow-sm">
              <FaMoneyBillWave className="w-5 h-5" />
            </div>
          </div>
          <button
            onClick={handleViewAllSites}
            className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between text-[13px] font-bold text-primary hover:text-primary/80 transition-colors"
          >
            <span>View All Sites</span>
          </button>
        </div>

        {/* Dynamic Stage Cards */}
        {Object.entries(stageConfig).map(([key, config]) => {
          const stageData = stageSummary.find((s) => s.stage === key);
          const currentAmount = stageData?.currentAmount || 0;
          const percentageValue = parseFloat(stageData?.percentageChange || "0");
          const isPositive = percentageValue >= 0;

          return (
            <div key={key} className="premium-card p-6 flex flex-col justify-between group relative overflow-hidden h-[185px]">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-[13px] font-bold text-gray-400 uppercase tracking-widest mb-1">{config.label}</p>
                  <h2 className="text-3xl font-extrabold text-gray-800">
                    ₹<CountUp end={Number(currentAmount)} duration={1.4} formattingFn={formatCountShort} />
                  </h2>
                  {stageData ? (
                    (() => {
                      const percentageChange = stageData?.percentageChange || "0.00%";
                      const isNegative = percentageChange.includes("-") || parseFloat(percentageChange) < 0;
                      return (
                        <p className={`text-[12px] mt-2 flex items-center font-bold ${isNegative ? "text-red-500" : "text-green-500"}`}>
                          {isNegative ? <FaArrowDown /> : <FaArrowUp />}
                          <span className="ml-1">{percentageChange}</span>
                        </p>
                      );
                    })()
                  ) : (
                    <p className="text-[12px] mt-2 text-gray-400 font-medium italic">No activity yet</p>
                  )}
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm ${config.bg} ${config.text}`}>
                  {React.cloneElement(config.icon, { className: 'w-5 h-5' })}
                </div>
              </div>
              <button
                onClick={() => handleViewAllByStage(key)}
                className={`mt-4 pt-4 border-t border-gray-50 flex items-center justify-between text-[13px] font-bold ${config.text} hover:opacity-80 transition-opacity`}
              >
                <span>View All {config.label}</span>
              </button>
            </div>
          );
        })}
      </div>

      {/* tables row 1*/}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        {/* Today Deals Status */}
        <div className="bg-white shadow-lg rounded-xl overflow-hidden">
          <div className="bg-[#f1f4f9] px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-base font-semibold text-[#405189]">
              Today Deals Status
            </h2>
          </div>

          {/* Table Scrollable Area */}
          <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead className="sticky top-0 z-10 text-[13px] bg-[#f9fafc] text-[#405189] uppercase tracking-wide shadow-sm">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold">
                    Quotation
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">Name</th>
                  <th className="px-5 py-3 text-left font-semibold">
                    Sales Person
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">Status</th>
                  <th className="px-5 py-3 text-left font-semibold">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {todayDeals.length > 0 ? (
                  todayDeals.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition">
                      <td className="px-5 py-4 font-medium text-md text-gray-900 whitespace-nowrap">
                        QTN-{item.quotation_No}
                      </td>
                      <td className="px-5 py-4 text-md">{item.name ?? "-"}</td>
                      <td className="px-5 py-4 text-md">
                        {item.salepersonname}
                      </td>
                      <td className="px-5 py-4 text-md">
                        <span
                          className={`text-md font-semibold inline-block px-2 py-1 rounded-full
                                      bg-${statusColorMap[
                            item.status.toLowerCase()
                            ] ?? "gray"
                            }-100 
                                      text-${statusColorMap[
                            item.status.toLowerCase()
                            ] ?? "gray"
                            }-800`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-semibold text-gray-700 text-md">
                        ₹{Number(item.dealValue).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center text-gray-400 px-5 py-8 font-medium"
                    >
                      No deals found for today.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* My Tasks */}
        <div className="bg-white shadow-lg rounded-xl overflow-hidden flex flex-col">
          <div className="bg-[#f1f4f9] px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-base font-semibold text-[#405189]">My Tasks</h2>
            <div className="flex items-center gap-3">
              <button
                className="text-gray-600 hover:text-black"
                onClick={() => fetchMyTasks(selectedSalePersonId)}
                title="Refresh"
              >
                <TbRefresh
                  className={`w-5 h-5 ${taskLoading ? "animate-spin text-teal-500" : ""
                    }`}
                />
              </button>
              <button
                className="bg-[#405189] hover:bg-[#32406f] text-white px-4 py-1.5 rounded text-sm"
                onClick={() => navigate(`/calendar`)}
              >
                + Add Task
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
            <table className="min-w-full text-sm text-gray-700 border-collapse">
              <thead className="sticky top-0 z-10 text-[13px] bg-[#f9fafc] text-[#405189] uppercase tracking-wide shadow-sm">
                <tr>
                  <th className="py-3 px-5 text-left font-semibold">Task</th>
                  <th className="py-3 px-5 text-left font-semibold">
                    Description
                  </th>
                  <th className="py-3 px-5 text-left font-semibold">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tasks.length > 0 ? (
                  tasks.map((task, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition">
                      <td className="py-4 px-5 font-medium text-gray-900 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="form-checkbox text-teal-600 text-md"
                            checked={task.isComplete}
                            readOnly
                          />
                          {task.subject}
                        </div>
                      </td>
                      <td className="py-4 px-5 text-gray-600 text-md">
                        {task.description}
                      </td>
                      <td className="py-4 px-5 text-md text-gray-500 whitespace-nowrap">
                        {formatDateIndian(task.date)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center text-gray-400 py-8 font-medium"
                    >
                      No tasks available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t text-right">
            <button
              onClick={() => navigate("/calendar")}
              className="text-sm text-[#405189] font-medium hover:underline"
            >
              Show More →
            </button>
          </div>
        </div>
      </div>

      {/* tables row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        {/* Top Customers */}
        <div className="bg-white shadow-lg rounded-xl overflow-hidden flex flex-col">
          <div className="bg-[#f1f4f9] px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-[#405189]">
              Top Customers
            </h2>
          </div>
          <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead className="sticky top-0 bg-[#f9fafc] z-10 text-[#405189] text-[13px] uppercase tracking-wide shadow-sm">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold">
                    Customer
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">
                    Mobile No
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">
                    Total Project
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topCustomers.length > 0 ? (
                  topCustomers.map((item, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition">
                      <td className="px-5 py-4 font-medium text-md text-gray-900 whitespace-nowrap">
                        {item.customerName}
                      </td>
                      <td className="px-5 py-4 text-md">{item.mobile}</td>
                      <td className="px-5 py-4 text-md">{item.totalProject}</td>
                      <td className="px-5 py-4 text-md font-semibold text-gray-700">
                        ₹{Number(item.revenue).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center text-gray-400 py-6 font-medium"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Architects */}
        <div className="bg-white shadow-lg rounded-xl overflow-hidden flex flex-col">
          <div className="bg-[#f1f4f9] px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-[#405189]">
              Top Architects
            </h2>
          </div>
          <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead className="sticky top-0 bg-[#f9fafc] z-10 text-[#405189] text-[13px] uppercase tracking-wide shadow-sm">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold">
                    Architect
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">Mobile</th>
                  <th className="px-5 py-3 text-left font-semibold">
                    Projects
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topArchitects.length > 0 ? (
                  topArchitects.map((item, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition">
                      <td className="px-5 py-4 font-medium text-gray-900 whitespace-nowrap text-md">
                        {item.architect_Name}
                      </td>
                      <td className="px-5 py-4 text-md">{item.mobile}</td>
                      <td className="px-5 py-4 text-md">
                        {item.totalProjects}
                      </td>
                      <td className="px-5 py-4 font-semibold text-md text-gray-700">
                        ₹{Number(item.revenue).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center text-gray-400 py-6 font-medium"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* chart  */}
      <div className="bg-white rounded-xl shadow mt-10">
        <div className="bg-[#f1f4f9] px-6 py-4 border-b border-gray-200 rounded-t-xl">
          <h2 className="text-base font-semibold text-[#405189]">
            Monthly Performance
          </h2>
        </div>
        <div className="w-full overflow-x-auto">
          <div className="min-w-[700px] h-[400px] mt-6">
            <Chart type="bar" data={data} options={optionsBar} />
          </div>
        </div>
      </div>

      {/* tables row 3 */}
      <div className="bg-white shadow-lg rounded-xl overflow-hidden mt-10">
        <div className="bg-[#f1f4f9] px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-[#405189]">
            In-Production Overview
          </h2>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full table-auto border-collapse">
            <thead className="sticky top-0 bg-[#f9fafc] z-10 text-[#405189] text-[13px] uppercase tracking-wide shadow-sm">
              <tr>
                <th className="px-5 py-3 text-left font-semibold">
                  Quotation No
                </th>
                <th className="px-5 py-3 text-left font-semibold">Site Name</th>
                <th className="px-5 py-3 text-left font-semibold">
                  Total Window
                </th>
                <th className="px-5 py-3 text-left font-semibold">Amount</th>
                <th className="px-5 py-3 text-left font-semibold">
                  Complete (%)
                </th>
              </tr>
            </thead>
            <tbody>{renderInProductionTable()}</tbody>
          </table>
        </div>
      </div>

      {/* chart and table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        {/* Status Breakdown - Polar Chart */}
        <div className="bg-white shadow-lg rounded-xl overflow-hidden flex flex-col">
          <div className="bg-[#f1f4f9] px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-[#405189]">
              Status Breakdown
            </h2>
          </div>

          {/* Chart */}
          <div className="flex-1 p-6 flex items-center justify-center">
            <div className="w-full max-w-[500px]">
              <PolarArea data={polarChartData} options={options} />
            </div>
          </div>
        </div>

        {/* Lost Deals Table */}
        <div className="bg-white shadow-lg rounded-xl overflow-hidden flex flex-col">
          <div className="bg-[#f1f4f9] px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-[#405189]">
              Lost Deals
            </h2>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead className="sticky top-0 bg-[#f9fafc] z-10 text-[#405189] text-[13px] uppercase tracking-wide shadow-sm">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold">
                    Quotation No
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">
                    Site Name
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">Reason</th>
                  <th className="px-5 py-3 text-left font-semibold">
                    Final Amount
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">
                    Close Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lostDeals.length > 0 ? (
                  lostDeals.map((item, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition">
                      <td className="px-5 py-4 font-medium text-gray-900 whitespace-nowrap">
                        {item.quotationNo}
                      </td>
                      <td className="px-5 py-4 text-md">{item.site_Name}</td>
                      <td className="px-5 py-4 text-md">{item.reason}</td>
                      <td className="px-5 py-4 text-md">
                        ₹{Number(item.finalAmount).toLocaleString("en-IN")}
                      </td>
                      <td className="px-5 py-4 text-md text-gray-500 whitespace-nowrap">
                        {formatDateIndian(item.quotationDate)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center text-gray-400 py-6 font-medium"
                    >
                      No lost deals found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
